import { useEffect, useState } from 'react';
import { useReducedMotion } from '@ai-tutor/ui';
import { useNow } from '@/hooks/ui/useNow';
import { formatClock } from '@/lib/focus/format';
import {
  BREATHING_PATTERNS,
  breathAt,
  type BreathingPatternId,
} from './breathingPatterns';
import {
  BreathingView,
  type BreathingDuration,
  type BreathingStatus,
} from './BreathingView';

interface BreathingExerciseProps {
  compact?: boolean;
  defaultPattern?: BreathingPatternId;
  defaultDuration?: BreathingDuration;
}

/**
 * The guided breathing exercise: owns the session clock and hands
 * `BreathingView` the current cue. A session is short and does not survive
 * navigation, on purpose - it is something you do now, not something to
 * resume.
 */
export function BreathingExercise({
  compact = false,
  defaultPattern = 'cyclic',
  defaultDuration = '1',
}: BreathingExerciseProps) {
  const [pattern, setPattern] = useState<BreathingPatternId>(defaultPattern);
  const [duration, setDuration] = useState<BreathingDuration>(defaultDuration);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [status, setStatus] = useState<BreathingStatus>('idle');
  const reducedMotion = useReducedMotion();
  const running = status === 'running' && startedAt !== null;
  // 200ms: the cue has to change on the beat, not up to a second late.
  const now = useNow(running, 200);

  const totalMs = Number(duration) * 60_000;
  const elapsed = running ? now - startedAt : 0;
  const finished = running && elapsed >= totalMs;

  useEffect(() => {
    if (finished) {
      setStatus('done');
      setStartedAt(null);
    }
  }, [finished]);

  const position = breathAt(BREATHING_PATTERNS[pattern], elapsed);

  return (
    <BreathingView
      pattern={pattern}
      onPatternChange={setPattern}
      duration={duration}
      onDurationChange={setDuration}
      status={status}
      cue={position.step.cue}
      scale={position.step.scale}
      stepSeconds={position.step.seconds}
      stepProgress={position.stepProgress}
      remainingLabel={formatClock(totalMs - elapsed)}
      reducedMotion={reducedMotion}
      onStart={() => {
        setStartedAt(Date.now());
        setStatus('running');
      }}
      onStop={() => {
        setStartedAt(null);
        setStatus('idle');
      }}
      compact={compact}
    />
  );
}
